import { API_HOST } from '@/constants/config';

// SSR-safe agents API client used by `generateStaticParams` and other
// build-time SEO paths (`/agents/category/[slug]`, `/agents/[slug]`).
// Direct fetch with no Zustand so it works inside Node during static export.
//
// `getBySlug` uses `credentials: 'include'` in the browser so optional
// auth cookies reach `/json/agents/by-slug/:slug` for private agents.
//
// Runtime/auth-bound calls still live in `lib/api.js` (`agentsApi`).

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchAgents = async (path, params = {}, fetchOptions = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;

  const { credentials } = fetchOptions;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
      ...(credentials ? { credentials } : {}),
    });
  } catch (networkErr) {
    throw new Error('Network error — unable to reach the agents service.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Agents service returned an unexpected response.'
        : `Agents service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch agents data');
  }

  return data;
};

export const agentApiSsr = {
  // Enabled agent categories. Drives `generateStaticParams` for
  // /agents/category/[slug].
  listCategories: async () => {
    const data = await fetchAgents('/json/agent-categories');
    return { categories: data.categories || [] };
  },

  // Per-category payload (category + first page of agents). Used at build
  // time to populate JSON-LD `ItemList` and provide a content-rich initial
  // HTML render even before the client-side store hydrates.
  getCategoryWithAgents: async (slug, { page = 1 } = {}) => {
    if (!slug) return null;
    const data = await fetchAgents(
      `/json/agent-categories/${encodeURIComponent(slug)}/agents`,
      { page },
    );
    return data || null;
  },

  /** Public slug list for `generateStaticParams` on `/agents/[slug]`. */
  listSeoSlugs: async () => {
    const data = await fetchAgents('/json/agents/seo-slugs');
    return data.slugs || [];
  },

  /** Public agent detail by URL slug (optional cookies on web). */
  getBySlug: async (slug) => {
    if (!slug) return null;
    const path = `/json/agents/by-slug/${encodeURIComponent(slug)}`;
    const fetchOpts = typeof window !== 'undefined' ? { credentials: 'include' } : {};
    const data = await fetchAgents(path, {}, fetchOpts);
    return data.agent || null;
  },
};
